import { ReactNode, useContext, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Input, Button, Select, message } from "antd";
import dayjs from "dayjs";
import { useTask, useUpdateTask } from "../../queries/tasks";
import { useProjects } from "../../queries/projects";
import { useSprints } from "../../queries/sprints";
import { useAddSubtask, useUpdateSubtask, useDeleteSubtask } from "../../queries/subtasks";
import { useStaffDirectory, StaffMember } from "../../queries/users";
import { TaskStatus, SubTaskProps, ProjectProps, SprintProps } from "../../types/interfaces";
import { SEVERITY_OPTIONS, PRIORITY_OPTIONS } from "../../utils/levels";
import { ApiError } from "../../hooks/apiRequest";
import { DisplayComponent } from "../../navigation/layout/DisplayComponent";
import { PageShell } from "../../components/ui/PageShell";
import { TaskStatusTag, PriorityTag, SeverityTag, StatusTag, TASK_STATUS } from "../../components/ui/StatusTag";
import { EmptyState } from "../../components/ui/States";
import AuthContext from "../../context/AuthContext";
import styles from "./task.module.css";

const SUBTASK_STATUSES: TaskStatus[] = ["backlog", "ready", "in_progress", "in_review", "done"];

const Fact = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className={styles.fact}>
    <span className="osi-kicker">{label}</span>
    <span className={styles.factValue}>{children}</span>
  </div>
);

const None = () => <span className={styles.factEmpty}>Not set</span>;

export const TaskDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { userData } = useContext(AuthContext);
  const isStaff = userData?.role === "manager" || userData?.role === "agent";
  const { data: task, isLoading, isError, error } = useTask(id!);
  const { data: users } = useStaffDirectory({ enabled: isStaff });
  const { data: projects } = useProjects();
  const { data: sprints } = useSprints();
  const updateTask = useUpdateTask();
  const addSubtask = useAddSubtask(id!);
  const updateSubtask = useUpdateSubtask(id!);
  const deleteSubtask = useDeleteSubtask(id!);
  const [newSubtaskTitle, setNewSubtaskTitle] = useState("");

  const handleAddSubtask = async () => {
    if (!newSubtaskTitle.trim()) return;
    try {
      await addSubtask.mutateAsync({ title: newSubtaskTitle.trim() });
      setNewSubtaskTitle("");
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : "Could not add the subtask");
    }
  };

  const userName = (uid: string | null) => (users || []).find((u: StaffMember) => u._id === uid)?.name;
  const saveError = (err: unknown) => message.error(err instanceof ApiError ? err.message : "Could not save the change");
  const done = (task?.subtasks || []).filter((s: SubTaskProps) => s.status === "done").length;
  const overdue = task?.dueDate && task.status !== "done" && dayjs(task.dueDate).isBefore(dayjs(), "day");

  return (
    <DisplayComponent error={isError ? error : undefined} isLoading={isLoading}>
      {task && (
        <PageShell
          kind="detail"
          crumbs={[
            { label: "Projects", to: "/projects" },
            { label: "Board", to: `/projects/${task.projectId}/board` },
            { label: task._id.slice(-4).toUpperCase() },
          ]}
          title={task.title}
          meta={[
            { label: "Status", value: <TaskStatusTag status={task.status} /> },
            { label: "Severity", value: <SeverityTag severity={task.severity} /> },
            { label: "Priority", value: <PriorityTag priority={task.priority} /> },
            ...(task.isBlocked ? [{ label: "Flag", value: <StatusTag label="Blocked" tone="danger" dot /> }] : []),
            { label: "Points", value: task.storyPoints != null ? <span className="osi-num">{task.storyPoints}</span> : <None /> },
          ]}
          actions={
            <Button onClick={() => navigate(`/projects/${task.projectId}/board`)}>Back to board</Button>
          }
        >
          <div className={styles.split}>
            <div className={styles.main}>
              <div className="osi-kicker" style={{ marginBottom: 8 }}>Description</div>
              <p className={styles.desc}>
                {task.description || "No description yet — add one before this moves to Ready."}
              </p>
            </div>
            <aside className={styles.rail}>
              <Fact label="Project">
                <Link to={`/projects/${task.projectId}/board`}>
                  {(projects || []).find((p: ProjectProps) => p._id === task.projectId)?.name || "Project"}
                </Link>
              </Fact>
              <Fact label="Sprint">
                {isStaff ? (
                  <Select
                    size="small"
                    allowClear
                    style={{ width: "100%" }}
                    placeholder="Not in a sprint"
                    value={task.sprintId ?? undefined}
                    options={(sprints || []).filter((s: SprintProps) => s.status !== "completed").map((s: SprintProps) => ({ label: s.name, value: s._id }))}
                    onChange={(sprintId) => updateTask.mutate({ id: task._id, data: { sprintId: sprintId || null } }, { onError: saveError })}
                  />
                ) : (sprints || []).find((s: SprintProps) => s._id === task.sprintId)?.name || <None />}
              </Fact>
              {task.ticketId && (
                <Fact label="Source ticket"><Link to={`/support/tickets/${task.ticketId}`}>View ticket →</Link></Fact>
              )}
              {isStaff && (
                <>
                  <Fact label="Severity">
                    <Select size="small" style={{ width: "100%" }} value={task.severity} options={SEVERITY_OPTIONS}
                      onChange={(severity) => updateTask.mutate({ id: task._id, data: { severity } }, { onError: saveError })} />
                  </Fact>
                  <Fact label="Priority">
                    <Select size="small" style={{ width: "100%" }} value={task.priority} options={PRIORITY_OPTIONS}
                      onChange={(priority) => updateTask.mutate({ id: task._id, data: { priority } }, { onError: saveError })} />
                  </Fact>
                </>
              )}
              <Fact label="Assignee">
                {isStaff ? (
                  <Select
                    size="small"
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    style={{ width: "100%" }}
                    placeholder="Unassigned"
                    value={task.assigneeId ?? undefined}
                    options={(users || []).map((u: StaffMember) => ({ label: u.name, value: u._id }))}
                    onChange={(assigneeId) => updateTask.mutate({ id: task._id, data: { assigneeId: assigneeId || null } }, { onError: saveError })}
                  />
                ) : (userName(task.assigneeId) || <None />)}
              </Fact>
              <Fact label="Reporter">{userName(task.reporterId) || <None />}</Fact>
              <Fact label="Due">
                {task.dueDate
                  ? <span className="osi-num" style={{ color: overdue ? "var(--osi-danger)" : undefined }}>
                      {dayjs(task.dueDate).format("D MMM YYYY")}
                    </span>
                  : <None />}
              </Fact>
              <Fact label="Labels">
                {task.labels?.length
                  ? <span style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                      {task.labels.map((l: string) => <StatusTag key={l} label={l} tone="neutral" />)}
                    </span>
                  : <None />}
              </Fact>
            </aside>
          </div>

          <section>
            <div className={styles.sectionHead}>
              <span className={styles.sectionTitle}>Subtasks</span>
              <span className={styles.sectionCount}>
                {task.subtasks.length ? `${done} of ${task.subtasks.length} done` : "none yet"}
              </span>
            </div>

            <div className={styles.subtasks}>
              {task.subtasks.length === 0 ? (
                <div style={{ padding: 24 }}>
                  <EmptyState
                    title="No subtasks"
                    description="Break the task down if it's more than a day's work — subtasks carry their own status."
                  />
                </div>
              ) : (
                task.subtasks.map((subtask: SubTaskProps) => (
                  <div className={styles.sub} key={subtask._id}>
                    <span className={`${styles.subTitle} ${subtask.status === "done" ? styles.subDone : ""}`}>
                      {subtask.title}
                    </span>
                    <Select
                      size="small"
                      style={{ width: 110 }}
                      aria-label="Subtask severity"
                      value={subtask.severity}
                      options={SEVERITY_OPTIONS}
                      onChange={(severity) =>
                        updateSubtask.mutate({ subtaskId: subtask._id, severity }, { onError: saveError })
                      }
                    />
                    <Select
                      size="small"
                      style={{ width: 100 }}
                      aria-label="Subtask priority"
                      value={subtask.priority}
                      options={PRIORITY_OPTIONS}
                      onChange={(priority) =>
                        updateSubtask.mutate({ subtaskId: subtask._id, priority }, { onError: saveError })
                      }
                    />
                    <Select
                      size="small"
                      style={{ width: 140 }}
                      value={subtask.status}
                      options={SUBTASK_STATUSES.map((s) => ({ label: TASK_STATUS[s].label, value: s }))}
                      onChange={(status) =>
                        updateSubtask.mutate(
                          { subtaskId: subtask._id, status },
                          { onError: (err) => message.error(err instanceof ApiError ? err.message : "Could not update the subtask") }
                        )
                      }
                    />
                    <Button
                      size="small"
                      danger
                      onClick={() =>
                        deleteSubtask.mutate(subtask._id, {
                          onError: (err) => message.error(err instanceof ApiError ? err.message : "Could not delete the subtask"),
                        })
                      }
                    >
                      Delete
                    </Button>
                  </div>
                ))
              )}
              <div className={styles.adder}>
                <Input
                  value={newSubtaskTitle}
                  placeholder="Add a subtask"
                  onChange={(e) => setNewSubtaskTitle(e.target.value)}
                  onPressEnter={handleAddSubtask}
                />
                <Button type="primary" onClick={handleAddSubtask} loading={addSubtask.isPending}>Add</Button>
              </div>
            </div>
          </section>
        </PageShell>
      )}
    </DisplayComponent>
  );
};
